


























































import { PostColor } from 'types'
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { namespace, State, Getter } from 'vuex-class'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import BeethovenAttachmentMetadataSlope from 'vuecomponents/beethoven-attachment-metadata-slope.vue'

import { getDisplayAttributes } from 'bits/beethoven'
import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { getHostname } from 'bits/url'
import { secondsToSemiColonedText } from 'bits/time'
import { FallbackAttachmentType, fallbackImageUrl, fallbackAttachmentSizeMapping } from 'bits/attachments'

import { processedUrl } from '@padlet/vivaldi-client'

function getStylizedProviderName(name): string {
  if (!name) return ''
  // This is done to comply with Youtube's requirements in order to use their player on Padlet.
  if (name.toLowerCase() === 'youtube') return name
  if (name.toLowerCase() === 'spotify') return name
  if (name.toLowerCase() === 'instagram') return name
  return name.toLowerCase()
}

/**
 * @displayName Beethoven Attachment Preview
 * Barebone component that fetches Beethoven Data and loads the preview image:
 * 1. Fetches preview info from beethoven.
 * 2. Preview Data is loaded, emit `load-data` then load image using ImageThumbnail
 * 3. Image is loaded, emit `load-image`
 */
@Component({ components: { ImageThumbnail, BeethovenAttachmentMetadataSlope } })
export default class BeethovenAttachmentPreview extends Mixins() {
  @Prop() url!: string
  @Prop({ default: 508 }) width!: number
  @Prop({ default: undefined }) height!: number | undefined
  @Prop({ required: true }) usageContext!: 'postModal' | 'postView'
  /**
   * Show border in case the preview is loaded on a background that requires it so that it won't blend in too much.
   * For example set this to true if loading a PDF on the white modal.
   */
  @Prop({ default: false }) xBorder!: boolean
  /**
   * Default to show metadata slope, can hide if needed
   */
  @Prop({ default: true }) xMetadata!: boolean
  /**
   * How the component should decide to show dark mode: Using browser or with custom isDark prop
   */
  @Prop({ default: 'browser' }) darkThemeType!: 'browser' | 'custom'
  @Prop({ default: 'browser' }) isDark!: false
  @Prop({ default: true }) lazyLoading!: boolean
  @Prop({ default: 'default' }) backgroundColor!: 'default' | NonNullable<PostColor>
  @Prop({ default: '' }) borderRadiusClass!: string
  @Prop({ default: false }) xHoverOverlay!: boolean
  @State isMobile!: boolean

  @State dpr!: number
  @Getter dir!: string
  @Getter useArvo!: boolean
  @Getter('isStream') isWallInStreamFormat!: boolean
  @Getter('isCanvas') isWallInCanvasFormat!: boolean
  @(namespace('browser').Getter) isPhone!: boolean
  @(namespace('attachment').Action) updateLinkDisplayAttributes

  link: BeethovenDisplayAttributes | null = null
  isLoaded = false
  isError = false
  isImageLoaded = false

  // Note: need to initialize these values for reactivity of getter based on them to work
  imageUrl: string | null = null
  imageColor: string | null = null
  // Default to size of fallback image (images/fallback_file.png)
  thumbnailWidth = 3000
  thumbnailHeight = 1500

  get isFile(): boolean {
    return this.link?.content_category === 'file'
  }

  get isImage(): boolean {
    return this.link?.content_category === 'photo'
  }

  get isVideo(): boolean {
    return this.link?.content_category === 'video'
  }

  get isUploadedVideo(): boolean {
    return this.link?.content_category === 'video' && this.link?.provider_name.toLowerCase() === 'padlet drive'
  }

  get isWebpage(): boolean {
    return this.link?.content_category === 'page'
  }

  get isPadletWall(): boolean {
    // provider_name for a wall is the builder's name, "padlet" should be used only for landside pages (e.g. padlet.com, padlet.com/features)
    return (
      this.link?.content_subcategory === 'padlet' &&
      !!this.link?.provider_name &&
      this.link.provider_name.toLowerCase() !== 'padlet'
    )
  }

  get isYoutube(): boolean {
    return this.link?.provider_name.toLowerCase() === 'youtube'
  }

  get isInstagram(): boolean {
    return this.link?.provider_name.toLowerCase() === 'instagram'
  }

  get isGoogleDoc(): boolean {
    return (
      this.link?.provider_name.toLowerCase() === 'google docs' ||
      this.link?.provider_name.toLowerCase() === 'docs.google.com'
    )
  }

  get isAudio(): boolean {
    return this.link?.content_category === 'audio'
  }

  get isUploadedAudio(): boolean {
    return this.link?.content_category === 'audio' && this.link?.provider_name.toLowerCase() === 'padlet drive'
  }

  get isSpreadsheet(): boolean {
    return this.link?.content_subcategory === 'spreadsheet'
  }

  get isPresentation(): boolean {
    return this.link?.content_subcategory === 'presentation'
  }

  get isDocument(): boolean {
    return this.link?.content_category === 'document'
  }

  get isSpotify(): boolean {
    return this.link?.provider_name.toLowerCase() === 'spotify'
  }

  get isLoadingFallbackImage(): boolean {
    return this.isError || this.link?.is_fallback || (!!this.imageUrl && this.imageUrl.startsWith('data:'))
  }

  get previewImageSrc(): string {
    if (!!this.imageUrl && !this.isLoadingFallbackImage) return this.imageUrl
    const fallbackImgSrc = fallbackImageUrl(
      this.fallbackAttachmentType,
      this.isDark ? 'dark' : 'light',
      this.backgroundColor === 'default' ? null : this.backgroundColor,
    )
    if (this.isWallInCanvasFormat) return fallbackImgSrc
    return processedUrl(fallbackImgSrc, { width: this.fallbackWidth })
  }

  get fallbackWidth(): number {
    if (this.isWallInStreamFormat) return 1032 // 2 x 516
    return 476 // 2 x 28
  }

  get isCroppingVideoPreview(): boolean {
    return this.isVideo && !!this.link && !!this.link.image_aspect_ratio
  }

  get videoCropRatio(): number {
    return this.link?.image_aspect_ratio || 4 / 3
  }

  get fallbackAttachmentType(): FallbackAttachmentType {
    if (!this.link) {
      return FallbackAttachmentType.BrokenLink
    }
    if (this.isAudio) return FallbackAttachmentType.Audio
    if (this.isVideo) return FallbackAttachmentType.Video
    if (this.isImage) return FallbackAttachmentType.Image
    if (this.isFile) return FallbackAttachmentType.File
    if (this.isSpreadsheet) return FallbackAttachmentType.Spreadsheet
    if (this.isPresentation) return FallbackAttachmentType.Presentation
    if (this.isGoogleDoc) return FallbackAttachmentType.GoogleDocument
    if (this.isDocument) return FallbackAttachmentType.TextDocument
    if (this.isWebpage) return FallbackAttachmentType.Link
    return FallbackAttachmentType.Link
  }

  get hasMetadata(): boolean {
    if (!this.link) return false
    if (this.isInstagram) return true
    return !this.isImage && this.isImageLoaded
  }

  get metadataText(): string {
    if (!this.link) return ''
    if (this.isPadletWall) return this.__('Padlet')
    if (this.isSpotify) return getStylizedProviderName(this.link.provider_name)
    if (this.isYoutube) return getStylizedProviderName(this.link.provider_name)
    if (this.isUploadedAudio) return this.__('Audio')
    if (this.isUploadedVideo) return this.__('Video')
    if (this.isWebpage) {
      return (
        getHostname(this.link.url).replace(/^www\./, '') ||
        getStylizedProviderName(this.link.provider_name) ||
        this.__('webpage')
      )
    }
    if (this.isFile && this.link.metadata?.pretty_size) return this.link.extension.toUpperCase()
    if (this.isFile) return this.link.extension.toUpperCase()

    return this.link.extension.toUpperCase() || getStylizedProviderName(this.link.provider_name) || ''
  }

  get durationMetadataText(): string {
    if (!this.link) return ''
    if (this.link.metadata?.duration) return secondsToSemiColonedText(this.link.metadata?.duration, true)
    return ''
  }

  get extraMetadataText(): string {
    if (!this.link) {
      return ''
    }

    const attachmentHasDuration =
      (this.isSpotify || this.isYoutube || this.isUploadedAudio || this.isUploadedVideo) && !!this.durationMetadataText
    const attachmentHasFileSize = this.isFile && !!this.link?.metadata?.pretty_size

    if (this.isPadletWall) {
      return getStylizedProviderName(this.link.provider_name)
    } else if (attachmentHasDuration) {
      return this.durationMetadataText
    } else if (attachmentHasFileSize) {
      return this.link.metadata?.pretty_size as string
    }
    return ''
  }

  get padletAttachmentBottomBorderRadiusStyle(): Record<string, string> {
    // We used to have the exaggerated bottom border radius for padlet attachments everywhere
    // but we moved the attachment remove button in post modal to the bottom => stop doing that for the post modal
    if (!this.isPadletWall || this.usageContext === 'postModal') {
      return {}
    }

    const borderRadiusAttribute = this.dir === 'rtl' ? 'borderBottomLeftRadius' : 'borderBottomRightRadius'

    return {
      [borderRadiusAttribute]: `${Math.floor((this.width * 20) / 100)}px`,
    }
  }

  @Watch('width')
  handleWidthChanged(): void {
    this.getBeethovenAttributes()
  }

  @Watch('url')
  handleAttachmentUrlChanged(): void {
    this.getBeethovenAttributes()
  }

  useSizeDataForFallbackImage(): void {
    this.isImageLoaded = true
    const fallbackImageSize = fallbackAttachmentSizeMapping[this.fallbackAttachmentType]
    this.thumbnailWidth = fallbackImageSize.width
    this.thumbnailHeight = fallbackImageSize.height
  }

  handleBeethovenError(error): void {
    this.$emit('error', error)
    this.isError = true
    this.useSizeDataForFallbackImage()
  }

  async getBeethovenAttributes(): Promise<void> {
    const locale = 'en'
    const bhOptions = {
      dpr: this.dpr,
      locale: locale,
      width: this.width,
      height: this.height,
      useArvo: this.useArvo,
    }

    this.isError = false

    let data
    try {
      data = await getDisplayAttributes(this.url, bhOptions)
      if (data === null) {
        this.handleBeethovenError('No data.')
      }
      if (data.error) {
        this.handleBeethovenError(data.error)
      }
    } catch (error) {
      this.handleBeethovenError(error)
    }

    if (this.isError) return

    this.updateLinkDisplayAttributes({ url: this.url, attributes: data })
    this.link = data
    this.isLoaded = true
    this.$emit('load-data', data)
    this.imageUrl = data.image_url
    this.imageColor = data.image_color
    this.thumbnailWidth = data.image_width
    this.thumbnailHeight = data.image_height
    if (this.isLoadingFallbackImage) {
      this.useSizeDataForFallbackImage()
    }
  }

  handleImageLoaded(): void {
    this.isImageLoaded = true
    this.$emit('load-image')
  }

  mounted(): void {
    this.getBeethovenAttributes()
  }
}
